<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { useNavigate } from 'svelte-navigator';
  import copy from 'clipboard-copy';
  import * as yup from 'yup';
  import dayjs from 'dayjs';
  import utc from 'dayjs/plugin/utc';
  import timezone from 'dayjs/plugin/timezone';
  import truncate from 'lodash/truncate';

  import {
    Accordion,
    AccordionItem,
    Button,
    Column,
    ContentSwitcher,
    CopyButton,
    FormGroup,
    Grid,
    InlineNotification,
    MultiSelect,
    Row,
    Switch,
    TooltipIcon,
    StructuredList,
    StructuredListHead,
    StructuredListRow,
    StructuredListCell,
    StructuredListBody,
    Tag,
    TextArea,
    TextInput,
  } from 'carbon-components-svelte';
  import { JsonView } from '@zerodevx/svelte-json-view';
  import Copy16 from 'carbon-icons-svelte/lib/Copy16';
  import Certificate16 from 'carbon-icons-svelte/lib/Certificate16';
  import Settings16 from 'carbon-icons-svelte/lib/Settings16';
  import Events16 from 'carbon-icons-svelte/lib/Events16';
  import Info16 from 'carbon-icons-svelte/lib/Information16';

  import { Form } from '@mst-fe/sveltejs-forms';

  import AddIcon from 'carbon-icons-svelte/lib/Add16';

  import ComboSelect from '../../../components/ComboSelect.svelte';
  import DoubleTag from '../../../components/DoubleTag.svelte';
  import LoadingButton from '../../../components/LoadingButton.svelte';
  import DataFeedSettingsPerInventoryModal from './DataFeedSettingsPerInventoryModal.svelte';
  import RawDataFeedPathDatesModal from '../RawDataFeedPathDatesModal.svelte';
  import RelativeDate from '../../../components/RelativeDate.svelte';
  import StatusIcon from '../../../components/StatusIcon.svelte';
  import StatusTooltip from '../../../components/StatusTooltip.svelte';
  import TooltipList from '../../../components/TooltipList.svelte';

  import { getDataFeedPermissionApprovals, updateDataFeed, getVenues } from '../../../services';
  import { getServerErrorMessage } from '../../../utils';
  import { DataInventoryServiceIds, DataInventoryServiceNames } from '../../../../shared/constants';
  import { getDataFeedEntitlementsReportUrl } from '../../reports/utils/entitlements-report-routing';
  import { PERMISSION_TYPE_LABEL_MAP } from '../../../utils/permission-types';

  const INITIAL_PATH_STATE = {
    path: '',
    issue: '',
  };
  const MESSAGES = {
    warningSelections: 'Typed destination is already under selections',
  };

  dayjs.extend(utc);
  dayjs.extend(timezone);

  export let dataFeedId, dataFeed;

  let formSubmitMessage,
    approvals = [],
    venues = [],
    pathsJsonViewActive = false,
    pathState = { ...INITIAL_PATH_STATE },
    modals = {
      dataFeedSettingsPerInventory: { open: false },
      rawDataFeedPathDates: { open: false },
    };

  const dispatch = createEventDispatcher();
  const navigate = useNavigate();

  const schema = yup.object().shape({
    notes: yup.string().nullable(),
    venueId: yup.string().nullable(),
    approvalServiceIds: yup.array().nullable(),
    paths: yup
      .array()
      .of(
        yup
          .string()
          .test('no-space', 'No space is allowed', (value) => /^\S*$/.test(value))
          .test('no-leading-slash', 'No leading slash is allowed', (value) => !value?.startsWith('/'))
          .test('no-trailing-slash', 'No trailing slash is allowed', (value) => !value?.endsWith('/'))
          .test('must-have-two-slashes', 'Must contain exactly two slashes (e.g., bucket/nic/feed)', (value) => {
            return (value?.match(/\//g) || []).length === 2;
          })
          .test('no-double-slash', 'No double slash is allowed', (value) => !value?.includes('//'))
          .test('invalid-bucket-name', 'Invalid bucket name; should start with "p-lseg-ll-" excluding "p-lseg-ll-feeds"', (value) => {
            /* eslint-disable-next-line no-unsafe-optional-chaining */
            const [bucketName] = value?.split('/');
            return /^(p-lseg-ll-|d-lseg-ll-)[a-z0-9.-]+$/.test(bucketName) && bucketName !== 'p-lseg-ll-feeds';
          })
      )
      .min(
        Number(!dataFeed.awsArbitratedPcapsAvailable && !dataFeed.awsLakeAvailable),
        'Raw Feed paths cannot be empty. Please provide at least one valid path: <bucket_name>/<path_to_feed>'
      ),
  });

  async function fetchDependencies() {
    try {
      [approvals, venues] = await Promise.all([getDataFeedPermissionApprovals(dataFeedId), getVenues()]);
    } catch (error) {
      formSubmitMessage = {
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to load dependencies! Please refresh page for retry.',
      };
    }
  }

  async function onFormSubmit({ detail: { values, setSubmitting } }) {
    setSubmitting(true);
    formSubmitMessage = undefined;
    try {
      await updateDataFeed(dataFeedId, values);
      dispatch('dataFeedUpdated');
      formSubmitMessage = {
        kind: 'success',
        title: 'Success:',
        subtitle: 'Data Feed updated successfully!',
      };
    } catch (error) {
      const errorMessage = getServerErrorMessage(error) ?? 'Verify your submission and try again.';
      console.error('[DataFeedBasicInformation] Failed to update data feed data!', errorMessage);
      formSubmitMessage = {
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to update data feed! ${errorMessage}`,
      };
    } finally {
      setSubmitting(false);
      document.querySelector('#main-content').scrollTo(0, 0);
    }
  }

  function handlePathState(key) {
    return function onPathStateChange({ detail: value }) {
      pathState = {
        ...pathState,
        [key]: value,
      };
    };
  }

  function handlePathAddition({ key, setValue, values }) {
    if (values.includes(pathState.path)) {
      pathState = {
        ...pathState,
        issue: 'warning',
      };
      return;
    }

    const newValues = [...values, pathState.path];
    setValue(key, newValues);
    pathState = { ...INITIAL_PATH_STATE };
  }

  function handlePathRemoval({ key, setValue, valueToRemove, values }) {
    const newValues = values.filter((value) => value !== valueToRemove);
    setValue(key, newValues);
  }

  function viewDataFeedSubscribers(permissionType) {
    navigate(getDataFeedEntitlementsReportUrl(dataFeedId, permissionType));
  }

  function openModal(name, forwardData) {
    modals = {
      ...modals,
      [name]: { open: true, data: forwardData },
    };
  }

  function closeModal(name) {
    modals = {
      ...modals,
      [name]: { open: false },
    };
  }

  onMount(fetchDependencies);

  // eslint-disable-next-line svelte/no-immutable-reactive-statements
  $: services = Object.keys(DataInventoryServiceIds).map((id) => ({
    id: DataInventoryServiceIds[id],
    text: DataInventoryServiceNames[id],
  }));

  $: venueOptions = venues.map((venue) => ({
    id: venue.id,
    text: `${venue.exchangeName} | ${venue.mic} | ${venue.countryCode}`,
    value: venue,
  }));

  $: venueTimezoneDateTime = dataFeed.venue?.timezone ? dayjs().tz(dataFeed.venue.timezone).format('YYYY/MM/DD HH:mm (Z)') : 'N/A';

  $: slasByServiceId = (dataFeed.slaTaskAssociations ?? []).reduce((acc, association) => {
    (acc[association.serviceId] ??= []).push({
      label: association.task?.friendlyName ?? association.taskId,
      href: `/sla/${association.taskId}`,
    });
    return acc;
  }, {});
</script>

{#if formSubmitMessage}
  <InlineNotification kind={formSubmitMessage.kind} lowContrast title={formSubmitMessage.title} subtitle={formSubmitMessage.subtitle} />
{/if}
<Grid noGutterLeft noGutterRight padding>
  <Row>
    <Column sm={4} md={8} lg={8}>
      <h2 class="column-heading">
        <div class="name-controls">
          <Button
            portalTooltip
            class="copy-button"
            hasIconOnly
            icon={Copy16}
            iconDescription="Copy to clipboard"
            tooltipPosition="top"
            tooltipAlignment="start"
            kind="ghost"
            size="small"
            on:click={() => copy(dataFeed.name)}
          />
          <div class="text">
            {dataFeed.name}
          </div>
        </div>
        {#if dataFeed.venue}
          <div>
            <DoubleTag label="MIC" value={dataFeed.venue?.mic} color="cyan" />
            <DoubleTag label={dataFeed.venue?.timezone} color="magenta" withCopyButton={false}>
              {venueTimezoneDateTime}
              <TooltipIcon
                tooltipText="Feed timezone time: Snapshot at page load only"
                direction="right"
                align="center"
                icon={Info16}
                portalTooltip
              />
            </DoubleTag>
          </div>
        {/if}
      </h2>
      <StructuredList condensed flush class="mst-structured-list--truncate">
        <StructuredListHead>
          <StructuredListRow head>
            <StructuredListCell head>Attribute</StructuredListCell>
            <StructuredListCell head>Value</StructuredListCell>
          </StructuredListRow>
        </StructuredListHead>
        <StructuredListBody>
          <StructuredListRow>
            <StructuredListCell noWrap>Internal ID</StructuredListCell>
            <StructuredListCell>
              <div class="id-controls">
                <div class="text">
                  {dataFeed.id}
                </div>
                <Button
                  portalTooltip
                  class="copy-button"
                  hasIconOnly
                  icon={Copy16}
                  iconDescription="Copy to clipboard"
                  kind="ghost"
                  size="small"
                  on:click={() => copy(dataFeed.id)}
                />
              </div>
            </StructuredListCell>
          </StructuredListRow>
          <StructuredListRow>
            <StructuredListCell noWrap>
              {#if slasByServiceId[DataInventoryServiceIds.AWS_LAKE]?.length}
                <div class="tooltip-list-wrapper">
                  <TooltipList
                    triggerText="AWS Lake"
                    title="SLA(s)"
                    items={slasByServiceId[DataInventoryServiceIds.AWS_LAKE]}
                    icon={Certificate16}
                  />
                </div>
              {:else}
                AWS Lake
              {/if}
            </StructuredListCell>
            <StructuredListCell>
              <div class="inventory-wrapper">
                <StatusTooltip valid={dataFeed.awsLakeAvailable}>
                  <div slot="validText">
                    {#if dataFeed.minAwsLakeAvailable && dataFeed.maxAwsLakeAvailable}
                      {dataFeed.minAwsLakeAvailable} — {dataFeed.maxAwsLakeAvailable}
                    {:else}
                      No availability
                    {/if}
                  </div>
                  <div slot="undefinedText">No availability</div>
                </StatusTooltip>
                <div class="invetory-controls">
                  {#if dataFeed.awsLakeAvailable}
                    <Button
                      portalTooltip
                      iconDescription="Subscribers"
                      icon={Events16}
                      tooltipPosition="left"
                      tooltipAlignment="end"
                      size="small"
                      kind="ghost"
                      on:click={() => viewDataFeedSubscribers('data-feeds-lake')}
                    />
                  {/if}
                  <Button
                    portalTooltip
                    iconDescription="Settings"
                    icon={Settings16}
                    tooltipPosition="top"
                    tooltipAlignment="end"
                    size="small"
                    kind="ghost"
                    on:click={() =>
                      openModal('dataFeedSettingsPerInventory', {
                        dataFeedId,
                        dataFeedName: dataFeed.name,
                        serviceId: DataInventoryServiceIds.AWS_LAKE,
                      })}
                  />
                </div>
              </div>
            </StructuredListCell>
          </StructuredListRow>
          <StructuredListRow>
            <StructuredListCell noWrap>
              {#if slasByServiceId[DataInventoryServiceIds.AWS_ARBITRATED_PCAPS]?.length}
                <div class="tooltip-list-wrapper">
                  <TooltipList
                    triggerText="AWS Arbitrated PCAPs"
                    title="SLA(s)"
                    items={slasByServiceId[DataInventoryServiceIds.AWS_ARBITRATED_PCAPS]}
                    icon={Certificate16}
                  />
                </div>
              {:else}
                AWS Arbitrated PCAPs
              {/if}
            </StructuredListCell>
            <StructuredListCell>
              <div class="inventory-wrapper">
                <StatusTooltip valid={dataFeed.awsArbitratedPcapsAvailable}>
                  <div slot="validText">
                    {#if dataFeed.minAwsArbitratedPcapsAvailable && dataFeed.maxAwsArbitratedPcapsAvailable}
                      {dataFeed.minAwsArbitratedPcapsAvailable} — {dataFeed.maxAwsArbitratedPcapsAvailable}
                    {:else}
                      No availability
                    {/if}
                  </div>
                  <div slot="undefinedText">No availability</div>
                </StatusTooltip>
                <div class="invetory-controls">
                  {#if dataFeed.awsArbitratedPcapsAvailable}
                    <Button
                      iconDescription="Subscribers"
                      portalTooltip
                      icon={Events16}
                      tooltipPosition="left"
                      tooltipAlignment="end"
                      size="small"
                      kind="ghost"
                      on:click={() => viewDataFeedSubscribers('data-feeds-aws-arbitrated-pcaps')}
                    />
                  {/if}
                  <Button
                    portalTooltip
                    iconDescription="Settings"
                    icon={Settings16}
                    tooltipPosition="top"
                    tooltipAlignment="end"
                    size="small"
                    kind="ghost"
                    on:click={() =>
                      openModal('dataFeedSettingsPerInventory', {
                        dataFeedId,
                        dataFeedName: dataFeed.name,
                        serviceId: DataInventoryServiceIds.AWS_ARBITRATED_PCAPS,
                      })}
                  />
                </div>
              </div>
            </StructuredListCell>
          </StructuredListRow>
          <StructuredListRow>
            <StructuredListCell noWrap>
              {#if slasByServiceId[DataInventoryServiceIds.AWS_RAW_FEEDS]?.length}
                <div class="tooltip-list-wrapper">
                  <TooltipList
                    triggerText="AWS Raw Feeds"
                    title="SLA(s)"
                    items={slasByServiceId[DataInventoryServiceIds.AWS_RAW_FEEDS]}
                    icon={Certificate16}
                  />
                </div>
              {:else}
                AWS Raw Feeds
              {/if}
            </StructuredListCell>
            <StructuredListCell>
              <div class="inventory-wrapper">
                <StatusIcon valid={dataFeed.awsRawFeedsAvailable} />
                <div class="invetory-controls">
                  {#if dataFeed.awsRawFeedsAvailable}
                    <Button
                      portalTooltip
                      iconDescription="Subscribers"
                      icon={Events16}
                      tooltipPosition="left"
                      tooltipAlignment="end"
                      size="small"
                      kind="ghost"
                      on:click={() => viewDataFeedSubscribers('data-feeds-aws-raw')}
                    />
                  {/if}
                  <Button
                    portalTooltip
                    iconDescription="Settings"
                    icon={Settings16}
                    tooltipPosition="top"
                    tooltipAlignment="end"
                    size="small"
                    kind="ghost"
                    on:click={() =>
                      openModal('dataFeedSettingsPerInventory', {
                        dataFeedId,
                        dataFeedName: dataFeed.name,
                        serviceId: DataInventoryServiceIds.AWS_RAW_FEEDS,
                      })}
                  />
                </div>
              </div>
            </StructuredListCell>
          </StructuredListRow>
        </StructuredListBody>
      </StructuredList>
      <h2 class="column-heading">Metadata</h2>
      <StructuredList condensed flush>
        <StructuredListHead>
          <StructuredListRow head>
            <StructuredListCell head>Attribute</StructuredListCell>
            <StructuredListCell head>Value</StructuredListCell>
          </StructuredListRow>
        </StructuredListHead>
        <StructuredListBody>
          <StructuredListRow>
            <StructuredListCell noWrap>Created</StructuredListCell>
            <StructuredListCell>
              <RelativeDate date={dataFeed.createdAt} />
            </StructuredListCell>
          </StructuredListRow>
          <StructuredListRow>
            <StructuredListCell noWrap>Created By</StructuredListCell>
            <StructuredListCell>{dataFeed.createdBy}</StructuredListCell>
          </StructuredListRow>
          <StructuredListRow>
            <StructuredListCell noWrap>Updated</StructuredListCell>
            <StructuredListCell>
              <RelativeDate date={dataFeed.updatedAt} />
            </StructuredListCell>
          </StructuredListRow>
          <StructuredListRow>
            <StructuredListCell noWrap>Updated By</StructuredListCell>
            <StructuredListCell>{dataFeed.updatedBy}</StructuredListCell>
          </StructuredListRow>
        </StructuredListBody>
      </StructuredList>
      {#if approvals?.length}
        <h2 class="column-heading">Approvals</h2>
        <div class="approvals">
          <Accordion>
            {#each approvals as approval}
              <AccordionItem>
                <svelte:fragment slot="title">
                  <strong
                    >{approval.permissionDetails?.group?.name ?? 'Unknown'}{approval.permissionDetails?.type
                      ? ` | ${PERMISSION_TYPE_LABEL_MAP[approval.permissionDetails?.type]}`
                      : ''}</strong
                  >
                  <div>
                    {approval.permissionDataFeedDetails?.startDate
                      ? `${approval.permissionDataFeedDetails?.startDate} - ${approval.permissionDataFeedDetails?.endDate}`
                      : 'Expired or deleted'}{approval.permissionDataFeedDetails?.isOngoing ? ' | On going' : ''}{approval
                      .permissionDataFeedDetails?.isDayEntitlement
                      ? ` | Day entitlement${
                          approval.permissionDataFeedDetails?.expiryDate
                            ? `, expires at: ${approval.permissionDataFeedDetails.expiryDate}`
                            : ''
                        }`
                      : ''}
                  </div>
                </svelte:fragment>
                <div>
                  <div class="approval-link">
                    <TextInput readonly labelText="Link to approval" placeholder="No link for this approval" value={approval.url} />
                    {#if approval.url}
                      <CopyButton text={approval.url} feedback="Copied to clipboard" copy={(text) => copy(text)} />
                    {/if}
                  </div>
                  <TextArea readonly labelText="Note" placeholder="No note for this approval" value={approval.note} />
                </div>
              </AccordionItem>
            {/each}
          </Accordion>
        </div>
      {/if}
    </Column>
    <Column sm={4} md={8} lg={8}>
      <h2 class="column-heading column-heading-form">Editable Fields</h2>
      <Form
        fieldDomString="input,select,textarea,div[name]"
        {schema}
        initialValues={{
          ...dataFeed,
          paths: dataFeed.paths ?? [],
        }}
        validateOnBlur={true}
        validateOnChange={true}
        on:submit={onFormSubmit}
        let:submitForm
        let:isSubmitting
        let:setValue
        let:errors
        let:touched
        let:values
      >
        <FormGroup>
          <ComboSelect
            labelText="Venue"
            name="venueId"
            invalid={touched.venueId && !!errors.venueId}
            invalidText={errors.venueId}
            items={venueOptions}
            placeholder="Select a venue"
            selectedId={values.venueId}
            helperText="Please change with caution as this will affect alerts generated on delayed data."
            on:change={({ detail: { selectedId } }) => setValue('venueId', selectedId)}
            let:item
          >
            {item.text}
            <svelte:fragment slot="item-details">
              Exchange: {item.value.exchangeName} | MIC: {item.value.mic} | Country: {item.value.countryCode} | Timezone:
              {item.value.timezone}
            </svelte:fragment>
          </ComboSelect>
        </FormGroup>
        <FormGroup legendText="AWS Path to add">
          <div class="upload-controls">
            <div class="path-input">
              <TextInput
                autocomplete="off"
                value={pathState.path}
                on:change={handlePathState('path')}
                placeholder="e.g. <bucket_name>/<nic>/<feed>"
              />
            </div>
            <Button
              disabled={!pathState.path}
              size="field"
              iconDescription="Add path"
              icon={AddIcon}
              on:click={handlePathAddition({ key: 'paths', setValue, values: values.paths })}
            />
          </div>
          {#if pathState.issue}
            <span class="{pathState.issue} bx--label">{MESSAGES[`${pathState.issue}Selections`]}</span>
          {/if}
          <div class="selections-wrapper">
            <div class="paths-view-header">
              <span class="bx--label" class:bx--label--required={!dataFeed.awsArbitratedPcapsAvailable && !dataFeed.awsLakeAvailable}>
                Selected path(s)
              </span>
              <ContentSwitcher
                aria-label="Paths view switch"
                size="sm"
                selectedIndex={pathsJsonViewActive ? 1 : 0}
                on:change={({ detail }) => {
                  pathsJsonViewActive = detail === 1;
                }}
              >
                <Switch text="Main" />
                <Switch text="JSON" />
              </ContentSwitcher>
            </div>
            {#if !pathsJsonViewActive}
              <div
                class={touched.paths && !!errors.paths ? 'error selections' : 'selections'}
                name="paths"
                aria-label="Created paths"
                role="listbox"
                tabindex="0"
              >
                {#if !values.paths?.length}
                  <span>Use input above to add paths. Your selections will appear here.</span>
                {:else}
                  {#each values.paths as value}
                    <div class="destination-tag">
                      <Tag
                        filter
                        on:close={handlePathRemoval({
                          key: 'paths',
                          setValue,
                          valueToRemove: value,
                          values: values.paths,
                        })}
                      >
                        <strong
                          class="path-label"
                          {...value.length > 65 ? { 'data-path': value } : {}}
                          on:click={openModal('rawDataFeedPathDates', {
                            path: value,
                          })}
                        >
                          {truncate(value, { length: 65 })}
                        </strong>
                      </Tag>
                    </div>
                  {/each}
                {/if}
              </div>
            {:else}
              <div class={touched.paths && !!errors.paths ? 'error paths-json-view-container' : 'paths-json-view-container'}>
                <div class="paths-json-action-bar">
                  <Button
                    iconDescription="Copy to clipboard"
                    icon={Copy16}
                    kind="ghost"
                    tooltipPosition="right"
                    size="small"
                    on:click={() => copy(JSON.stringify(values.paths ?? [], null, 2))}
                  />
                </div>
                <div class="paths-json-view">
                  <JsonView json={values.paths ?? []} />
                </div>
              </div>
            {/if}
            {#if touched.paths && !!errors.paths}
              <span class="error bx--label">{Array.isArray(errors.paths) ? errors.paths.filter(Boolean).join(', ') : errors.paths}</span>
            {:else if !pathsJsonViewActive && values.paths?.length}
              <div class="bx--form__helper-text">Click on a path to edit its date range.</div>
            {/if}
          </div>
        </FormGroup>
        <FormGroup>
          <MultiSelect
            filterable
            helperText="A reminder to get approval will appear for this feed while provisioning for a client, only for the selected service(s)."
            items={services}
            name="approvalServiceIds"
            placeholder="Request approval"
            spellcheck="false"
            labelText="Request approval"
            selectedIds={values.approvalServiceIds}
            on:select={({ detail: { selectedIds } }) => setValue('approvalServiceIds', selectedIds)}
          />
          <TextArea
            invalid={touched.notes && !!errors.notes}
            invalidText={errors.notes}
            labelText="Notes"
            name="notes"
            placeholder="Additional notes"
            type="text"
            value={values.notes}
            on:change={({ target }) => setValue('notes', target.value)}
          />
        </FormGroup>
        <div class="form-controls">
          <LoadingButton kind="primary" on:click={submitForm} disabled={isSubmitting} isLoading={isSubmitting}>Save</LoadingButton>
        </div>
      </Form>
    </Column>
  </Row>
</Grid>

{#if modals.rawDataFeedPathDates.open}
  <RawDataFeedPathDatesModal open {...modals.rawDataFeedPathDates.data} on:close={() => closeModal('rawDataFeedPathDates')} />
{/if}

{#if modals.dataFeedSettingsPerInventory.open}
  <DataFeedSettingsPerInventoryModal
    open
    {...modals.dataFeedSettingsPerInventory.data}
    on:close={() => closeModal('dataFeedSettingsPerInventory')}
  />
{/if}

<style>
  .inventory-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .approvals {
    max-height: 225px;
    overflow-y: auto;
  }
  .info {
    font-size: 12px;
    color: var(--cds-text-helper);
  }
  /* For accessibility, keep the column-heading elements as h2 tags, but style them similar to h4 */
  .column-heading {
    font-size: 1.25rem;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1.4;
    margin-bottom: 1rem;
  }

  .id-controls {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .name-controls {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .tooltip-list-wrapper :global(.bx--tooltip__label) {
    position: relative;
    padding-left: 1.5rem;
  }

  .tooltip-list-wrapper :global(.bx--tooltip__trigger) {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  .invetory-controls {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .approval-link {
    display: flex;
    align-items: flex-end;
  }

  .column-heading + :global(.bx--structured-list) {
    margin-bottom: 2.5rem;
  }
  .column-heading + :global(.bx--structured-list:last-child) {
    margin-bottom: 0.5rem;
  }
  .column-heading + :global(.bx--structured-list .bx--structured-list-td) {
    width: 50%;
  }

  .column-heading-form + :global(.sveltejs-forms > .bx--fieldset > .bx--form-item:first-child) {
    margin: 0 0 0.75rem;
  }

  .upload-controls {
    display: flex;
    align-items: flex-end;
  }

  .upload-controls .path-input {
    margin: 0 0.25rem 0 0;
    flex: 2;
  }

  .paths-view-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }

  .paths-view-header :global(.bx--content-switcher) {
    max-width: 14rem;
    height: 1rem;
  }

  .paths-view-header :global(.bx--label) {
    margin-bottom: 0;
  }

  .paths-view-header :global(.bx--content-switcher__label) {
    font-size: 0.75rem;
  }

  .selections-wrapper {
    margin-top: 0.75rem;
  }

  .selections {
    background-color: var(--cds-field-01);
    padding: 0.5rem;
    min-height: 100px;
    outline-offset: -2px;
  }

  .selections:focus {
    outline: 2px solid var(--cds-focus) !important;
  }

  .selections > span {
    color: var(--cds-text-helper);
    font-style: italic;
    font-size: 12px;
  }

  .selections :global(.bx--tag) {
    border: 1px solid var(--cds-ui-04);
  }

  .warning.bx--label {
    color: var(--outline-warning-color);
  }

  .error.bx--label {
    color: var(--cds-support-01);
  }

  .error.selections {
    outline: 2px solid var(--cds-support-01);
  }

  .paths-json-view-container {
    overflow-y: hidden;
    border: 1px solid var(--cds-layer-accent);
  }

  .paths-json-action-bar {
    height: 32px;
    background-color: var(--cds-layer-accent);
  }

  .paths-json-view {
    --nodePaddingLeft: 1.5rem;
    padding: 1rem 1.25rem;
    background-color: var(--cds-field-01);
    word-break: break-word;
  }

  .paths-json-view :global(.key) {
    color: var(--cds-text-01);
  }
  .error.paths-json-view-container {
    border: 2px solid var(--cds-support-01);
  }

  .destination-tag :global(.bx--tag) {
    position: relative;
  }

  .path-label[data-path]:hover::after {
    content: attr(data-path);
    position: absolute;
    top: 1.25rem;
    left: -1rem;
    z-index: 1;
    background-color: var(--cds-tag-background-gray, #e0e0e0);
    border-radius: 0.9365rem;
    padding: 0.1rem 0.5rem;
    white-space: nowrap;
    cursor: default;
    border: 1px solid var(--cds-ui-04);
  }

  @media screen and (min-width: 66rem) {
    .column-heading-form {
      margin-bottom: 1.875rem;
    }
    .column-heading-form + :global(.sveltejs-forms) {
      margin-top: 0;
    }
  }

  .id-controls :global(.bx--btn svg > path) {
    fill: var(--cds-link-01) !important;
  }
</style>
